import { Loader2 } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from "react";
import { cn } from "@/lib/cn.ts";

type Variant = "primary" | "secondary" | "ghost" | "danger";
const variants: Record<Variant, string> = {
  primary: "bg-accent text-accent-fg hover:brightness-110",
  secondary: "bg-surface-2 text-fg border border-border hover:bg-border/60",
  ghost: "text-fg hover:bg-surface-2",
  danger: "bg-danger-soft text-danger border border-danger/30 hover:bg-danger/15",
};

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean; size?: "sm" | "md" }>(
  function Button({ className, variant = "primary", loading, size = "md", children, disabled, ...rest }, ref) {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          "relative inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none",
          size === "md" ? "h-10 px-4 text-sm" : "h-8 px-3 text-xs",
          variants[variant],
          // busy is not "unavailable": the spinner stays clearly visible
          loading && "disabled:opacity-80",
          className,
        )}
        {...rest}
      >
        {/* while busy the spinner takes the content's place (same size, so nothing shifts); an icon button
            no longer shows its icon and the spinner side by side */}
        {loading ? (
          <>
            <span className="invisible inline-flex items-center justify-center gap-2" aria-hidden>{children}</span>
            <Loader2 className="absolute size-4 animate-spin" aria-hidden />
          </>
        ) : children}
      </button>
    );
  },
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={cn(
        "h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-fg placeholder:text-fg-muted/70",
        "focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30",
        className,
      )}
      {...rest}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, ...rest }, ref) {
  return (
    <select
      ref={ref}
      className={cn("h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30", className)}
      {...rest}
    />
  );
});

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: string | undefined; error?: string | undefined; children: ReactNode; htmlFor?: string | undefined }) {
  return (
    <label className="block space-y-1.5" htmlFor={htmlFor}>
      <span className="text-sm font-medium">{label}</span>
      {children}
      {error ? <span className="block text-xs text-danger">{error}</span> : hint ? <span className="block text-xs text-fg-muted">{hint}</span> : null}
    </label>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("rounded-lg border border-border bg-surface p-6 shadow-sm", className)}>{children}</div>;
}

export function Alert({ tone = "info", children, className }: { tone?: "info" | "danger" | "success" | "warn"; children: ReactNode; className?: string }) {
  const tones = {
    info: "bg-accent-soft text-fg border-accent/30",
    danger: "bg-danger-soft text-danger border-danger/30",
    success: "bg-success-soft text-success border-success/30",
    warn: "bg-warn-soft text-fg border-warn/40",
  };
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn("rounded-md border px-3 py-2 text-sm", tones[tone], className)}>
      {children}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("size-5 animate-spin text-fg-muted", className)} aria-label="loading" />;
}

export function PageTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="space-y-1">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-fg-muted">{subtitle}</p>}
    </div>
  );
}

/** Six-digit code input: numeric keyboard, auto-trim, LTR digits even on RTL pages. */
export const CodeInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> & { onChange: (v: string) => void }>(
  function CodeInput({ onChange, className, ...rest }, ref) {
    return (
      <Input
        ref={ref}
        dir="ltr"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        className={cn("text-center font-mono text-xl tracking-[0.5em]", className)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        {...rest}
      />
    );
  },
);
